import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import api, { userAPI } from '../services/api';
import './UserProfile.css';

const UserProfile = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    avatar: '',
    is_verified: false
  });
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 50,
    offset: 0,
    hasMore: false
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async (search = '') => {
    try {
      setLoading(true);
      // Use the axios api client so the interceptor attaches the admin
      // Authorization: Bearer token — the server resolves the tenant (tf_tenant_virq)
      // from the admin JWT tenant_id via optionalAdminTenant. Raw fetch sent no headers.
      const params = { page: 1, limit: 50 };
      if (search) params.search = search;
      const apiResponse = await api.get('/api/auth/users', { params });
      const data = apiResponse.data;
      console.log('📊 Users API Response:', {
        success: data.success,
        userCount: data.data?.users?.length || 0,
        totalUsers: data.data?.pagination?.total_users || 0,
        sampleUser: data.data?.users?.[0]
      });
      
      if (data.success && data.data) {
        setUsers(data.data.users || []);
        setPagination({
          total: data.data.pagination?.total_users || 0,
          limit: data.data.pagination?.limit || 50,
          offset: (data.data.pagination?.current_page - 1) * (data.data.pagination?.limit || 50),
          hasMore: data.data.pagination?.has_more || false
        });
      } else {
        console.error('Failed to fetch users:', data.message || 'Unknown error');
        setUsers([]);
        setPagination({ total: 0, limit: 50, offset: 0, hasMore: false });
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
      setPagination({ total: 0, limit: 50, offset: 0, hasMore: false });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchUsers(searchTerm);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // Optional: debounce search for real-time filtering
    // For now, we'll require clicking search button
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        // Update existing user
        await userAPI.updateProfile(editingUser.id, formData);
        alert('User updated successfully!');
      } else {
        // Create new user
        await userAPI.create(formData);
        alert('User created successfully!');
      }
      
      setShowModal(false);
      setEditingUser(null);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      alert('Failed to save user. Please try again.');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name || '',
      email: user.email || '',
      phone: user.phone || '',
      avatar: user.avatar || '',
      is_verified: user.is_verified || false
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      try {
        await userAPI.delete(id);
        alert('User deleted successfully!');
        fetchUsers();
      } catch (error) {
        console.error('Failed to delete user:', error);
        alert('Failed to delete user. Please try again.');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      avatar: '',
      is_verified: false
    });
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingUser(null);
    resetForm();
  };

  const loadMoreUsers = async () => {
    try {
      const newOffset = pagination.offset + pagination.limit;
      const response = await userAPI.getAll(50, newOffset);
      setUsers(prev => [...prev, ...response.data]);
      setPagination(response.pagination);
    } catch (error) {
      console.error('Failed to load more users:', error);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading && users.length === 0) {
    return (
      <Layout title="User Management">
        <div className="loading">Loading users...</div>
      </Layout>
    );
  }

  return (
    <Layout title="App User Management">
      <div className="user-management">
        <div className="page-header">
          <h2>App User Management</h2>
          <div className="header-actions">
            <span className="total-count">{pagination.total} total users</span>
            <button 
              className="create-btn"
              onClick={() => setShowModal(true)}
            >
              + Create User
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-container" style={{ marginBottom: '20px', padding: '15px', background: '#fff', borderRadius: '8px' }}>
          <form onSubmit={handleSearch} style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Search by name, email, or username..."
              value={searchTerm}
              onChange={handleSearchChange}
              style={{
                flex: 1,
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <button
              type="submit"
              style={{
                padding: '10px 20px',
                background: '#6366f1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Search
            </button>
            {searchTerm && (
              <button
                type="button"
                onClick={() => {
                  setSearchTerm('');
                  fetchUsers('');
                }}
                style={{
                  padding: '10px 20px',
                  background: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Clear
              </button>
            )}
          </form>
        </div>

        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>USER ID</th>
                <th>NAME</th>
                <th>EMAIL</th>
                <th>STATUS</th>
                <th>SIGN-IN METHOD</th>
                <th>CREATED AT</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">No users found</td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.id}</td>
                    <td>
                      <div className="user-name">
                        <span className="name">{user.name || user.username || 'N/A'}</span>
                        {user.is_verified && (
                          <span className="verified-badge">✓</span>
                        )}
                      </div>
                    </td>
                    <td>{user.email || 'N/A'}</td>
                    <td>
                      <span className={`status ${user.is_verified ? 'verified' : 'unverified'}`}>
                        {user.is_verified ? 'Verified' : 'Unverified'}
                      </span>
                    </td>
                    <td>
                      <span className={`signin-method ${user.sign_in_method === 'Google' ? 'google' : 'email'}`}>
                        {user.sign_in_method || 'Email'}
                      </span>
                    </td>
                    <td>{formatDateTime(user.created_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEdit(user)}
                          title="Edit User"
                        >
                          ✏️
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDelete(user.id)}
                          title="Delete User"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination.hasMore && (
          <div className="load-more-container">
            <button 
              className="load-more-btn"
              onClick={loadMoreUsers}
              disabled={loading}
            >
              {loading ? 'Loading...' : 'Load More Users'}
            </button>
          </div>
        )}

        <div className="pagination-info">
          Showing {users.length} of {pagination.total} users
        </div>

        {/* Modal for Create/Edit User */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>{editingUser ? 'Edit User' : 'Create New User'}</h3>
                <button className="close-btn" onClick={handleCancel}>×</button>
              </div>

              <form onSubmit={handleSubmit} className="user-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter full name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="Enter email address"
                    />
                  </div>

                  <div className="form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div className="form-group">
                    <label>Avatar URL (Optional)</label>
                    <input
                      type="url"
                      name="avatar"
                      value={formData.avatar}
                      onChange={handleInputChange}
                      placeholder="https://example.com/avatar.jpg (optional)"
                    />
                  </div>

                  <div className="form-group checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="is_verified"
                        checked={formData.is_verified}
                        onChange={handleInputChange}
                      />
                      <span className="checkmark"></span>
                      Verified User
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={handleCancel} className="cancel-btn">
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn">
                    {editingUser ? 'Update User' : 'Create User'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserProfile;